import { apiClient } from './client';
import type { EmployeeTeamRef } from './teams.api';

/**
 * Client for `/api/employees` and `/api/departments`. Mirror the shapes
 * in `server/src/routes/employees.routes.ts` +
 * `server/src/routes/departments.routes.ts` exactly — keep them in
 * lockstep when fields change.
 */

export interface Department {
  id: string;
  name: string;
  slug: string;
  parentDepartmentId: string | null;
  order: number;
  employeeCount: number;
}

export interface EmployeeRef {
  id: string;
  firstName: string;
  lastName: string;
}

export interface EmployeeListItem {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string | null;
  isActive: boolean;
  hireDate: string | null;
  departmentId: string | null;
  department: { id: string; name: string; slug: string } | null;
  managerEmployeeId: string | null;
  manager: EmployeeRef | null;
  membershipId: string | null;
  createdAt: string;
  /**
   * Cross-cutting teams this person is on (ADR-047). Orthogonal to
   * `department` — someone can run Customer Service AND sit on leadership.
   */
  teams: EmployeeTeamRef[];
}

export interface EmployeeListPage {
  data: EmployeeListItem[];
  total: number;
  limit: number;
  offset: number;
}

export interface EmployeeListQuery {
  departmentId?: string;
  status?: 'active' | 'inactive' | 'all';
  search?: string;
  limit?: number;
  offset?: number;
}

export async function listEmployees(q: EmployeeListQuery = {}): Promise<EmployeeListPage> {
  const params = new URLSearchParams();
  if (q.departmentId) params.set('departmentId', q.departmentId);
  if (q.status) params.set('status', q.status);
  if (q.search) params.set('search', q.search);
  if (typeof q.limit === 'number') params.set('limit', String(q.limit));
  if (typeof q.offset === 'number') params.set('offset', String(q.offset));
  const query = params.toString() ? `?${params.toString()}` : '';
  const res = await apiClient.get<EmployeeListPage>(`/api/employees${query}`);
  return res.data;
}

export interface CreateEmployeeRequest {
  firstName: string;
  lastName: string;
  email: string;
  jobTitle?: string;
  departmentId?: string;
  managerEmployeeId?: string;
  /** YYYY-MM-DD or ISO datetime. */
  hireDate?: string;
  isActive?: boolean;
  /** Teams to join on creation (ADR-047). */
  teamIds?: string[];
}

export async function createEmployee(body: CreateEmployeeRequest): Promise<EmployeeListItem> {
  const res = await apiClient.post<{ data: EmployeeListItem }>('/api/employees', body);
  return res.data.data;
}

/**
 * Detail shape returned by GET `/api/employees/:id`. Extends the list
 * shape with `reports` (direct reports). The server includes
 * department + manager objects on detail too, same as list.
 */
export type EmployeeDetail = EmployeeListItem & {
  reports: { id: string; firstName: string; lastName: string; jobTitle: string | null }[];
};

export async function getEmployee(id: string): Promise<EmployeeDetail> {
  const res = await apiClient.get<{ data: EmployeeDetail }>(`/api/employees/${encodeURIComponent(id)}`);
  return res.data.data;
}

/**
 * Partial-update payload. Mirrors the server's Zod schema:
 *   - firstName/lastName/jobTitle/isActive: string|boolean (skip with undefined).
 *   - departmentId/managerEmployeeId/hireDate: pass `null` to clear,
 *     a value to set, `undefined` to leave alone.
 *
 * Note: email is intentionally absent. Changing the email on an
 * existing record breaks the membership-to-employee link silently;
 * do that via a future explicit `transferEmail` flow instead.
 */
export interface UpdateEmployeeRequest {
  firstName?: string;
  lastName?: string;
  jobTitle?: string | null;
  departmentId?: string | null;
  managerEmployeeId?: string | null;
  /** YYYY-MM-DD or ISO datetime, or `null` to clear. */
  hireDate?: string | null;
  isActive?: boolean;
  /**
   * Replaces the person's FULL team list when present. Omit to leave team
   * membership untouched — sending `[]` removes them from every team.
   */
  teamIds?: string[];
}

export async function updateEmployee(id: string, body: UpdateEmployeeRequest): Promise<EmployeeListItem> {
  const res = await apiClient.put<{ data: EmployeeListItem }>(
    `/api/employees/${encodeURIComponent(id)}`,
    body,
  );
  return res.data.data;
}

/**
 * Soft-delete an employee. Server sets `isActive=false`; the row stays
 * for historical accountability-chart context.
 */
export async function deactivateEmployee(id: string): Promise<void> {
  await apiClient.delete(`/api/employees/${encodeURIComponent(id)}`);
}

export async function listDepartments(): Promise<Department[]> {
  const res = await apiClient.get<{ data: Department[] }>('/api/departments');
  return res.data.data;
}

export interface CreateDepartmentRequest {
  name: string;
  slug?: string;
  order?: number;
  parentDepartmentId?: string;
}

export async function createDepartment(body: CreateDepartmentRequest): Promise<Department> {
  const res = await apiClient.post<{ data: Department }>('/api/departments', body);
  return res.data.data;
}

export interface UpdateDepartmentRequest {
  name?: string;
  order?: number;
  parentDepartmentId?: string | null;
}

export async function updateDepartment(id: string, body: UpdateDepartmentRequest): Promise<Department> {
  const res = await apiClient.put<{ data: Department }>(`/api/departments/${encodeURIComponent(id)}`, body);
  return res.data.data;
}

export async function deleteDepartment(id: string): Promise<void> {
  await apiClient.delete(`/api/departments/${encodeURIComponent(id)}`);
}

export type IndustryTemplateKey = 'custom' | 'manufacturing' | 'saas' | 'services' | 'ecommerce' | 'agency';

export interface ApplyTemplateResponse {
  template: IndustryTemplateKey;
  addedSlugs: string[];
  skippedSlugs: string[];
}

export async function applyDepartmentTemplate(
  template?: IndustryTemplateKey,
): Promise<ApplyTemplateResponse> {
  const body = template ? { template } : {};
  const res = await apiClient.post<{ data: ApplyTemplateResponse }>('/api/departments/apply-template', body);
  return res.data.data;
}
